import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Camera, Plus, X, RotateCcw, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePlan } from '@/hooks/usePlan';
import { UpgradePrompt } from '@/components/shared/UpgradePrompt';
import { useLensDevices, useRegisterLensDevice, useRotateLensToken } from '@/api/queries/lens.queries';
import { useDepartments } from '@/api/queries/employee.queries';
import { cn } from '@/lib/cn';

const MODES = ['Entry', 'Exit', 'Both', 'Visitor'];

const MOCK_DEVICES = [
  { id: 'd1', name: 'Main Entrance', location: 'Ground Floor – Reception', mode: 'Both', status: 'ONLINE', last_seen: 'Just now', total_today: 47, model: 'Lens Pro v2', app_version: 'v2.1.4', android_id: 'AX-D1-NEXUS' },
  { id: 'd2', name: 'Engineering Floor', location: '3rd Floor – Wing A', mode: 'Entry', status: 'ONLINE', last_seen: '2 mins ago', total_today: 31, model: 'Lens Pro v2', app_version: 'v2.1.4', android_id: 'AX-D2-NEXUS' },
  { id: 'd3', name: 'Conference Room B', location: '2nd Floor', mode: 'Visitor', status: 'OFFLINE', last_seen: '3 hours ago', total_today: 0, model: 'Lens Lite v1', app_version: 'v1.8.2', android_id: 'AX-D3-NEXUS' },
  { id: 'd4', name: 'Cafeteria', location: 'Ground Floor', mode: 'Both', status: 'ONLINE', last_seen: '1 min ago', total_today: 89, model: 'Lens Pro v2', app_version: 'v2.1.4', android_id: 'AX-D4-NEXUS' },
];

const CHECKINS_BY_DEVICE = {
  d1: [
    { time: '09:02', name: 'Priya Mehta', type: 'IN' },
    { time: '09:15', name: 'Rahul Gupta', type: 'IN' },
    { time: '09:28', name: 'Ananya Sharma', type: 'IN' },
    { time: '12:30', name: 'Priya Mehta', type: 'OUT' },
    { time: '13:15', name: 'Priya Mehta', type: 'IN' },
    { time: '14:02', name: 'Rohit Verma', type: 'IN' },
  ],
  d4: [
    { time: '08:55', name: 'Ananya Sharma', type: 'IN' },
    { time: '09:10', name: 'Karan Patel', type: 'IN' },
  ],
};

// Fallback used only until the dynamic plan config (subscription-service)
// has loaded — once loaded, the real Super-Admin-editable limit from
// usePlan().limits.lens_device_limit takes over.
function staticQuotaFallback(plan) {
  if (plan === 'PRO') return 5;
  if (plan === 'CUSTOM') return Infinity;
  return 0; // BASIC
}

function AddDeviceModal({ onClose, onSuccess }) {
  const { mutate, isPending } = useRegisterLensDevice();
  const { data: deptResp } = useDepartments();
  const departments = deptResp?.data || [];
  const [form, setForm] = useState({ name: '', mode: 'Both', department_id: '' });
  const [token, setToken] = useState(null);

  const inputCls = 'w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';

  const handleAdd = () => {
    if (!form.name.trim()) { toast.error('Enter a device name'); return; }
    mutate(form, {
      onSuccess: (data) => {
        const generated = data?.data?.device_token || `LENS-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
        setToken(generated);
        onSuccess?.();
      },
      onError: () => toast.error('Failed to register device'),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={e => e.target === e.currentTarget && !token && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-md border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <h2 className="text-[18px] font-bold text-[var(--text-primary)]">
            {token ? 'Device Registered' : 'Add Lens Device'}
          </h2>
          {!token && (
            <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
          )}
        </div>

        {token ? (
          <div className="px-6 py-6 space-y-4">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-[12px] border border-green-200 dark:border-green-800">
              <p className="text-[13px] font-semibold text-green-700 dark:text-green-400 mb-1">One-time Device Token</p>
              <p className="text-[11px] text-green-600 dark:text-green-500 mb-3">
                Enter this in the Lens app on the Android device. It will not be shown again.
              </p>
              <div className="flex items-center gap-2 p-3 bg-white dark:bg-black/20 rounded-[8px] border border-green-300 dark:border-green-700">
                <code className="flex-1 text-[13px] font-mono font-bold text-[var(--text-primary)] break-all">{token}</code>
                <button onClick={() => { navigator.clipboard.writeText(token); toast.success('Token copied'); }}
                  className="text-[12px] font-semibold text-[var(--accent)] hover:underline shrink-0">Copy</button>
              </div>
            </div>
            <Button className="w-full" onClick={onClose}>Done</Button>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Device Name</label>
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                placeholder="e.g. Main Entrance" className={inputCls}/>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Mode</label>
                <select value={form.mode} onChange={e => setForm(f => ({...f, mode: e.target.value}))} className={inputCls}>
                  {MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Department</label>
                <select value={form.department_id} onChange={e => setForm(f => ({...f, department_id: e.target.value}))} className={inputCls}>
                  <option value="">All departments</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button className="flex-1" onClick={handleAdd} disabled={isPending}>
                {isPending ? 'Registering…' : 'Register Device'}
              </Button>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RotateConfirmDialog({ device, onConfirm, onClose, isPending }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center shrink-0">
            <AlertTriangle size={18} className="text-amber-600"/>
          </div>
          <div>
            <p className="text-[15px] font-bold text-[var(--text-primary)]">Rotate Token?</p>
            <p className="text-[12px] text-[var(--text-secondary)]">{device.name}</p>
          </div>
        </div>
        <p className="text-[13px] text-[var(--text-secondary)]">
          The current token will be invalidated immediately. The Lens app on this device will stop syncing until you enter the new token.
        </p>
        <div className="flex gap-2">
          <Button className="flex-1" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Rotating…' : 'Rotate Token'}
          </Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

function DeviceCard({ dev }) {
  const [expanded, setExpanded] = useState(false);
  const [showRotate, setShowRotate] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const { mutate: rotateToken, isPending: isRotating } = useRotateLensToken();
  const checkins = CHECKINS_BY_DEVICE[dev.id] || [];

  const handleRotate = () => {
    rotateToken({ id: dev.id }, {
      onSuccess: (data) => {
        const token = data?.data?.device_token || `LENS-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
        setNewToken(token);
        setShowRotate(false);
        toast.success('Token rotated — copy the new token before closing');
      },
      onError: () => toast.error('Failed to rotate token'),
    });
  };

  return (
    <>
      <div className="card p-5">
        <div className="flex items-center gap-4">
          <div className={cn(
            'w-12 h-12 rounded-[12px] flex items-center justify-center shrink-0',
            dev.status === 'ONLINE' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-[var(--bg-muted)]'
          )}>
            <Camera size={20} className={dev.status === 'ONLINE' ? 'text-[var(--success)]' : 'text-[var(--text-tertiary)]'}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[14px] font-semibold text-[var(--text-primary)]">{dev.name}</p>
              <Badge variant={dev.status === 'ONLINE' ? 'green' : 'red'} size="sm">{dev.status}</Badge>
              {dev.mode && <Badge variant="default" size="sm">{dev.mode}</Badge>}
            </div>
            <p className="text-[12px] text-[var(--text-secondary)]">{dev.location} · {dev.model}</p>
            <p className="text-[11px] text-[var(--text-tertiary)]">Last seen: {dev.last_seen}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-[20px] font-black text-[var(--text-primary)]">{dev.total_today}</p>
              <p className="text-[11px] text-[var(--text-tertiary)]">punches today</p>
            </div>
            <button onClick={() => setExpanded(!expanded)}
              className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)] transition-colors">
              {expanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'App Version', value: dev.app_version || 'v2.1.4' },
                { label: 'Android ID', value: dev.android_id || `AX-${dev.id.toUpperCase()}` },
                { label: 'Sync Status', value: dev.status === 'ONLINE' ? 'Live' : 'Disconnected' },
                { label: 'Mode', value: dev.mode || 'Both' },
              ].map(({ label, value }) => (
                <div key={label} className="p-2.5 bg-[var(--bg-muted)] rounded-[8px]">
                  <p className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wide">{label}</p>
                  <p className="text-[12px] font-semibold text-[var(--text-primary)] mt-0.5 truncate">{value}</p>
                </div>
              ))}
            </div>

            <div>
              <p className="text-[12px] font-semibold text-[var(--text-secondary)] mb-2">Today's Activity ({checkins.length})</p>
              {checkins.length > 0 ? (
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                  {checkins.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-[8px] bg-[var(--bg-muted)]">
                      <span className={cn(
                        'text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0',
                        c.type === 'IN'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      )}>{c.type}</span>
                      <span className="text-[12px] font-semibold text-[var(--text-primary)]">{c.name}</span>
                      <span className="ml-auto text-[11px] text-[var(--text-tertiary)]">{c.time}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[12px] text-[var(--text-tertiary)] py-4 text-center bg-[var(--bg-muted)] rounded-[8px]">
                  No activity recorded today
                </p>
              )}
            </div>

            {newToken && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-[10px] border border-green-200 dark:border-green-800">
                <p className="text-[12px] font-semibold text-green-700 dark:text-green-400 mb-1">New token (shown once):</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[12px] font-mono text-[var(--text-primary)] break-all">{newToken}</code>
                  <button onClick={() => { navigator.clipboard.writeText(newToken); toast.success('Copied'); }}
                    className="text-[11px] font-semibold text-[var(--accent)] hover:underline shrink-0">Copy</button>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button onClick={() => setShowRotate(true)}
                className="flex items-center gap-1.5 text-[12px] font-semibold text-amber-600 hover:text-amber-700 dark:text-amber-400 px-3 py-1.5 rounded-[8px] hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors">
                <RotateCcw size={13}/> Rotate Token
              </button>
            </div>
          </div>
        )}
      </div>

      {showRotate && (
        <RotateConfirmDialog
          device={dev}
          onConfirm={handleRotate}
          onClose={() => setShowRotate(false)}
          isPending={isRotating}
        />
      )}
    </>
  );
}

export default function LensPage() {
  const navigate = useNavigate();
  const { plan, hasFeature, limits } = usePlan();
  const [showAdd, setShowAdd] = useState(false);

  const { data: devicesResp } = useLensDevices();
  const devices = devicesResp?.data || MOCK_DEVICES;

  const quota = limits.lens_device_limit !== undefined
    ? (limits.lens_device_limit === null ? Infinity : limits.lens_device_limit)
    : staticQuotaFallback(plan);
  const online = devices.filter(d => d.status === 'ONLINE').length;
  const atQuota = devices.length >= quota;

  if (!hasFeature('selfie_attendance')) {
    return (
      <UpgradePrompt
        feature="Lens Devices"
        requiredPlan="Starter"
        description="Lens face-recognition kiosks are available on the Starter plan and above. Upgrade to connect attendance terminals."
      />
    );
  }

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Lens Devices"
        subtitle={`${online} online · ${devices.length}/${quota === Infinity ? '∞' : quota} devices used`}
        actions={
          atQuota && quota < Infinity ? (
            <Button size="sm" icon={Plus} disabled title={`${quota}-device limit reached`}>Add Device</Button>
          ) : (
            <Button size="sm" icon={Plus} onClick={() => setShowAdd(true)}>Add Device</Button>
          )
        }
      />

      {atQuota && quota < Infinity && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 text-[12px] text-amber-700 dark:text-amber-300">
          <AlertTriangle size={14} className="shrink-0"/>
          <span>
            You've reached your {quota}-device limit on the {plan} plan.{' '}
            <button onClick={() => navigate('/settings/plan')}
              className="font-semibold underline hover:no-underline">Upgrade</button>{' '}
            to add more.
          </span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="card text-center p-5">
          <p className="text-[28px] font-black text-[var(--success)]">{online}</p>
          <p className="text-[12px] text-[var(--text-secondary)]">Online</p>
        </div>
        <div className="card text-center p-5">
          <p className="text-[28px] font-black text-[var(--danger)]">{devices.length - online}</p>
          <p className="text-[12px] text-[var(--text-secondary)]">Offline</p>
        </div>
        <div className="card text-center p-5">
          <p className="text-[28px] font-black text-[var(--text-primary)]">
            {devices.reduce((s, d) => s + (d.total_today || 0), 0)}
          </p>
          <p className="text-[12px] text-[var(--text-secondary)]">Today's Punches</p>
        </div>
      </div>

      <div className="space-y-3">
        {devices.map(dev => <DeviceCard key={dev.id} dev={dev}/>)}
        {devices.length === 0 && (
          <div className="card py-12 text-center space-y-3">
            <Camera size={32} className="mx-auto text-[var(--text-tertiary)]"/>
            <p className="text-[14px] text-[var(--text-secondary)]">No Lens devices registered yet</p>
            <p className="text-[12px] text-[var(--text-tertiary)]">Register a device and enter the token in the Lens Android app</p>
            <Button size="sm" icon={Plus} onClick={() => setShowAdd(true)}>Add First Device</Button>
          </div>
        )}
      </div>

      {showAdd && <AddDeviceModal onClose={() => setShowAdd(false)} onSuccess={() => {}}/>}
    </div>
  );
}
